import { EventEmitter } from "@angular/core";
import { AuditLogInput } from "@app/audit-log/models/audit-log-type.model";
import { CHDevice, SecurityFobInfo, SecurityZoneInfo } from "@app/project/model/ch-device";
import { CHZWaveDevice } from "@app/project/model/ch-zwave-device";
import { CHDataActionPayload, CHSuccessActionCollectionResult, CHSuccessActionResult, CHUuid } from "@app/root-store/actions";
import { ClareHomeDbCollection } from "@app/shared/resources/resource-objectifier";
import { Action } from "@ngrx/store";
import { CHDataAction, CHDataSuccessAction, DataErrorAction } from "../../../shared/resources/data-actions";
import { DataServiceError } from "../../../shared/services/data-service-error";
import { DeviceClassStateObject } from "../clarehome.state";

export enum ActionTypes {
    GetDevices = "[Project Management] GetDevices",
    GetDevicesSilent = "[Project Management] GetDevicesSilent",
    GetDevicesSuccess = "[Project Management] GetDevicesSuccess",
    GetDevicesFailure = "[Project Management] GetDevicesFailure",
    GetDeviceInfoSuccess = "[Project Management] GetDeviceInfoSuccess",
    ResetDevicesCallState = "[Project Management] ResetDevicesCallState",
    ClearDevicesState = "[Project Management] ClearDevicesState",
    DeleteSensor = "[Project Mangement] DeleteSensor",
    DeleteSensorSuccess = "[Project Mangement] DeleteSensorSuccess",
    DeleteSensorFailure = "[Project Mangement] DeleteSensorFailure",
    DeleteSensorConfirmation = "[Project Mangement] DeleteSensorConfirmation",
    DeleteSensorConfirmationDismiss = "[Project Mangement] DeleteSensorConfirmationDismiss",
    DeleteDevice = "[Project Mangement] DeleteDevice",
    DeleteDeviceSuccess = "[Project Mangement] DeleteDeviceSuccess",
    DeleteDeviceFailure = "[Project Mangement] DeleteDeviceFailure",
    DeleteDeviceConfirmation = "[Project Mangement] DeleteDeviceConfirmation",
    DeleteAllDevices = "[Project Mangement] DeleteAllDevices",
    DeleteAllDevicesSuccess = "[Project Mangement] DeleteAllDevicesSuccess",
    DeleteAllDevicesFailure = "[Project Mangement] DeleteAllDevicesFailure",
    DeleteDeviceConfirmationDismiss = "[Project Mangement] DeleteDeviceConfirmationDismiss",
    UpdateDeviceName = "[Project Mangement] UpdateDeviceName",
    UpdateDeviceNameSuccess = "[Project Mangement] UpdateDeviceNameSuccess",
    UpdateDeviceNameFailure = "[Project Mangement] UpdateDeviceNameFailure",
    GetDeviceClassObjects = "[Project Management] GetDeviceClassObjects",
    GetDeviceClassObjectsForDevices = "[Project Management] GetDeviceClassObjectsForDevices",
    GetDeviceClassObjectsForDevicesSuccess = "[Project Management] GetDeviceClassObjectsForDevicesSuccess",
    GetDeviceClassObjectsForDevicesFailure = "[Project Management] GetDeviceClassObjectsForDevicesFailure",
    UpdateDeviceClassObject = "[Project Management] UpdateDeviceClassObject",
    InvokeDeviceClassObjectOperation = "[Project Management] InvokeDeviceClassObjectOperation",
    InvokeDeviceClassObjectOperationSilent = "[Project Management] InvokeDeviceClassObjectOperationSilent",
    InvokeDeviceClassObjectOperationSuccess = "[Project Management] InvokeDeviceClassObjectOperationSuccess",
    UpdateSecureDeviceClassObjectOperation = "[Project Management] UpdateSecureDeviceClassObjectOperation",
    UpdateSecureDeviceClassObjectOperationSilent = "[Project Management] UpdateSecureDeviceClassObjectOperationSilent",
    InvokeDeviceClassObjectOperationSilentSuccess = "[Project Management] InvokeDeviceClassObjectOperationSilentSuccess",
    UpdateDeviceClassObjectSuccess = "[Project Mangement] UpdateDeviceClassObjectSuccess",
    UpdateDeviceClassObjectFailure = "[Project Management] UpdateDeviceClassObjectFailure",
    GetDeviceClassObjectsSuccess = "[Project Mangement] GetDeviceClassObjectsSuccess",
    GetDeviceClassObjectsFailure = "[Project Mangement] GetDeviceClassObjectsFailure",
    GetDeviceClassObjectPropertySuccess = "[Project Mangement] GetDeviceClassObjectPropertySuccess",
    ClearDCOState = "[Project Management] ClearDCOState",
    ClearAllDCOs = "[Project Management] ClearAllDCOs",
    ResetDCOState = "[Project Management] ResetDCOState",
    SendSignalTestMessageSuccess = "[Project Management] SendSignalTestMessageSuccess",
    DeleteSecurityUserConfirmationDismiss = "DeleteSecurityUserConfirmationDismiss",
    DeleteSecurityUser = "DeleteSecurityUser",
    ResetAllSecurityUsersConfirmationDismiss = "ResetAllSecurityUsersConfirmationDismiss",
    ResetAllSecurityUsers = "ResetAllSecurityUsers",
    DeleteAllPINCodesConfirmationDismiss = "DeleteAllPINCodesConfirmationDismiss",
    DeleteAllPINCodes = "DeleteAllPINCodes",
    ClearZWaveDevicesState = "[Project Management] ClearZWaveDevicesState",
    GetZWaveDevices = "[Project Management] GetZWaveDevices",
    GetZWaveDevicesSuccess = "[Project Management] GetZWaveDevicesSuccess",
    GetZWaveDevicesFailure = "[Project Management] GetZWaveDevicesFailure",
}

export type DeviceAction = CHDataAction<CHDevice>;
export type ZWaveDeviceAction = CHDataAction<CHZWaveDevice>;
type DeviceSuccessAction = CHDataSuccessAction<CHDevice>;
type DeviceFailureAction = DataErrorAction<CHDevice | CHSuccessActionResult<CHDevice> | CHSuccessActionCollectionResult<CHDevice>>;
type DevicePayload = CHDataActionPayload & { device?: CHDevice; logAction?: AuditLogInput };
type DeviceResultPayload = CHSuccessActionCollectionResult<CHDevice>;
type DeviceError = DataServiceError<CHUuid & ({ result: CHDevice } | { result: ClareHomeDbCollection<CHDevice> })>;
type SensorError = DataServiceError<CHUuid & { result: SecurityZoneInfo }>;
type SecurityZoneInfoPayload = CHDataActionPayload & { securityZone: SecurityZoneInfo | SecurityFobInfo };
type ZWaveDevicePayload = CHDataActionPayload & { device?: CHZWaveDevice };
type ZWaveDeviceSuccessAction = CHDataSuccessAction<CHZWaveDevice>;
type ZWaveDeviceFailureAction = DataErrorAction<
    CHZWaveDevice | CHSuccessActionResult<CHZWaveDevice> | CHSuccessActionCollectionResult<CHZWaveDevice>
>;
type ZWaveDeviceResultPayload = CHSuccessActionCollectionResult<CHZWaveDevice>;
type ZWaveDeviceError = DataServiceError<CHUuid & ({ result: CHZWaveDevice } | { result: ClareHomeDbCollection<CHZWaveDevice> })>;

export class ResetDevicesCallState implements Action {
    readonly type = ActionTypes.ResetDevicesCallState;
}

export class ClearDevicesState implements Action {
    readonly type = ActionTypes.ClearDevicesState;
}

export class GetDevices implements DeviceAction {
    readonly type = ActionTypes.GetDevices;
    constructor(public readonly payload: DevicePayload) {}
}

export class GetDevicesSilent implements DeviceAction {
    readonly type = ActionTypes.GetDevicesSilent;
    constructor(public readonly payload: DevicePayload) {}
}

export class GetDevicesSuccess implements DeviceSuccessAction {
    readonly type = ActionTypes.GetDevicesSuccess;
    constructor(public readonly result: DeviceResultPayload) {}
}

export class GetDevicesFailure implements DeviceFailureAction {
    readonly type = ActionTypes.GetDevicesFailure;
    constructor(public readonly error: DeviceError) {}
}

export class GetDeviceInfoSuccess implements DeviceSuccessAction {
    readonly type = ActionTypes.GetDeviceInfoSuccess;
    constructor(public readonly result: DeviceResultPayload) {}
}

export class UpdateDeviceName implements DeviceAction {
    readonly type = ActionTypes.UpdateDeviceName;
    constructor(public payload: { uuid: string; deviceUid: string; deviceName: string }) {}
}

export class UpdateDeviceNameSuccess implements Action {
    readonly type = ActionTypes.UpdateDeviceNameSuccess;
    constructor(public result: { uuid: string; deviceUid: string; deviceName: string }) {}
}

export class UpdateDeviceNameFailure implements DeviceFailureAction {
    readonly type = ActionTypes.UpdateDeviceNameFailure;
    constructor(public readonly error: DeviceError) {}
}

export class DeleteDevice implements DeviceAction {
    readonly type = ActionTypes.DeleteDevice;
    constructor(public readonly payload: DevicePayload) {}
}

export class DeleteDeviceSuccess implements DeviceSuccessAction {
    readonly type = ActionTypes.DeleteDeviceSuccess;
    constructor(public readonly result: CHSuccessActionResult<CHDevice>) {}
}

export class DeleteDeviceFailure implements DeviceFailureAction {
    readonly type = ActionTypes.DeleteDeviceFailure;
    constructor(public readonly error: DeviceError) {}
}

export class DeleteDeviceConfirmation implements Action {
    readonly type = ActionTypes.DeleteDeviceConfirmation;
    constructor(public readonly payload: DevicePayload) {}
}

export class DeleteDeviceConfirmationDismiss implements Action {
    readonly type = ActionTypes.DeleteDeviceConfirmationDismiss;
    constructor(public payload: DevicePayload) {}
}

export class DeleteAllDevices implements DeviceAction {
    readonly type = ActionTypes.DeleteAllDevices;
    constructor(
        public readonly payload: {
            uuid: string;
            driverName: string;
            adapterName: string;
            addMethod: string;
            callbackEmitter?: EventEmitter<any>;
            logAction?: AuditLogInput;
        }
    ) {}
}

export class DeleteAllDevicesSuccess implements DeviceSuccessAction {
    readonly type = ActionTypes.DeleteAllDevicesSuccess;
    constructor(public readonly result: CHSuccessActionResult<CHDevice>) {}
}

export class DeleteAllDevicesFailure implements DeviceFailureAction {
    readonly type = ActionTypes.DeleteAllDevicesFailure;
    constructor(public readonly error: DeviceError) {}
}

export class DeleteSensor implements Action {
    readonly type = ActionTypes.DeleteSensor;
    constructor(public readonly payload: SecurityZoneInfoPayload) {}
}

export class DeleteSensorSuccess implements Action {
    readonly type = ActionTypes.DeleteSensorSuccess;
    constructor(public readonly result: CHSuccessActionResult<SecurityZoneInfo>) {}
}

export class DeleteSensorFailure implements Action {
    readonly type = ActionTypes.DeleteSensorFailure;
    constructor(public readonly error: SensorError) {}
}

export class DeleteSensorConfirmation implements Action {
    readonly type = ActionTypes.DeleteSensorConfirmation;
    constructor(public readonly payload: SecurityZoneInfoPayload) {}
}

export class DeleteSensorConfirmationDismiss implements Action {
    readonly type = ActionTypes.DeleteSensorConfirmationDismiss;
    constructor(public payload: SecurityZoneInfoPayload) {}
}

export class GetDeviceClassObjects implements Action {
    readonly type = ActionTypes.GetDeviceClassObjects;

    constructor(public payload: { uuid: string; deviceUid: string; dco: string; ignoreIDCheck?: boolean }) {}
}

export class GetDeviceClassObjectsSuccess implements Action {
    readonly type = ActionTypes.GetDeviceClassObjectsSuccess;

    constructor(
        public payload: {
            uuid: string;
            deviceClassObjects: ClareHomeDbCollection<DeviceClassStateObject>;
            deviceUid: string;
            deviceClass: string;
        }
    ) {}
}

export class GetDeviceClassObjectsFailure implements DataErrorAction<{ uuid: string; deviceUid: string; dco: string }> {
    readonly type = ActionTypes.GetDeviceClassObjectsFailure;
    constructor(public readonly error: DataServiceError<{ uuid: string; deviceUid: string; dco: string }>) {}
}

interface DeviceClassObjectsPayload {
    uuid: string;
    deviceUids: string[];
    dco: string;
    includeMetadata?: boolean;
}

export class GetDeviceClassObjectsForDevices implements Action {
    readonly type = ActionTypes.GetDeviceClassObjectsForDevices;

    constructor(public payload: DeviceClassObjectsPayload) {}
}

export class GetDeviceClassObjectsForDevicesSuccess implements Action {
    readonly type = ActionTypes.GetDeviceClassObjectsForDevicesSuccess;

    constructor(
        public payload: {
            uuid: string;
            deviceClassObjects: DeviceClassStateObject[];
        }
    ) {}
}

export class GetDeviceClassObjectsForDevicesFailure implements DataErrorAction<DeviceClassObjectsPayload> {
    readonly type = ActionTypes.GetDeviceClassObjectsForDevicesFailure;
    constructor(public readonly error: DataServiceError<any>, public readonly dco?: string) {}
}

export class UpdateDeviceClassObject implements Action {
    readonly type = ActionTypes.UpdateDeviceClassObject;

    constructor(
        public payload: {
            uuid: string;
            deviceUid: string;
            deviceClass: string;
            property: string;
            args: string;
            hideErrorToast?: boolean;
            callbackEmitter?: EventEmitter<Action>;
            logAction?: AuditLogInput;
            ignoreIDCheck?: boolean;
        }
    ) {}
}

export class InvokeDeviceClassObjectOperation implements Action {
    readonly type = ActionTypes.InvokeDeviceClassObjectOperation;

    constructor(
        public payload: {
            uuid: string;
            deviceUid: string;
            deviceClass: string;
            operation: string;
            args?: string[];
            callbackEmitter?: EventEmitter<Action>;
            logAction?: AuditLogInput;
        }
    ) {}
}

export class InvokeDeviceClassObjectOperationSilent implements Action {
    readonly type = ActionTypes.InvokeDeviceClassObjectOperationSilent;

    constructor(
        public payload: {
            uuid: string;
            deviceUid: string;
            deviceClass: string;
            operation: string;
            args?: string[];
            callbackEmitter?: EventEmitter<Action>;
        }
    ) {}
}

export class InvokeDeviceClassObjectOperationSuccess implements Action {
    readonly type = ActionTypes.InvokeDeviceClassObjectOperationSuccess;
}

export class InvokeDeviceClassObjectOperationSilentSuccess implements Action {
    readonly type = ActionTypes.InvokeDeviceClassObjectOperationSilentSuccess;
}

export class SendSignalTestMessageSuccess implements Action {
    readonly type = ActionTypes.SendSignalTestMessageSuccess;
}

export class UpdateSecureDeviceClassObjectOperation implements Action {
    readonly type = ActionTypes.UpdateSecureDeviceClassObjectOperation;

    constructor(
        public payload: {
            uuid: string;
            deviceUid: string;
            deviceClass: string;
            property: string;
            args?: string[];
            callbackEmitter?: EventEmitter<Action>;
        }
    ) {}
}

export class UpdateSecureDeviceClassObjectOperationSilent implements Action {
    readonly type = ActionTypes.UpdateSecureDeviceClassObjectOperationSilent;

    constructor(
        public payload: {
            uuid: string;
            deviceUid: string;
            deviceClass: string;
            property: string;
            args?: string[];
            callbackEmitter?: EventEmitter<Action>;
        }
    ) {}
}

export class UpdateDeviceClassObjectSuccess implements Action {
    readonly type = ActionTypes.UpdateDeviceClassObjectSuccess;

    constructor(
        public payload: {
            uuid: string;
            deviceUid: string;
            deviceClass: string;
            propertyKey: string;
            propertyValue;
        }
    ) {}
}

export class GetDeviceClassObjectPropertySuccess implements Action {
    readonly type = ActionTypes.GetDeviceClassObjectPropertySuccess;

    constructor(
        public payload: {
            uuid: string;
            deviceUid: string;
            deviceClass: string;
            propertyKey: string;
            propertyValue;
        }
    ) {}
}

export class UpdateDeviceClassObjectFailure implements Action {
    readonly type = ActionTypes.UpdateDeviceClassObjectFailure;
    constructor(public readonly error: SensorError, public readonly hideToast?: boolean) {}
}

export class ClearDCOState implements Action {
    readonly type = ActionTypes.ClearDCOState;

    constructor(public payload: { deviceUid: string; deviceClass?: string }) {}
}

export class ClearAllDCOs implements Action {
    readonly type = ActionTypes.ClearAllDCOs;
}

export class ResetDCOState implements Action {
    readonly type = ActionTypes.ResetDCOState;

    constructor(public payload: { deviceUid: string; deviceClass?: string }) {}
}

export class DeleteSecurityUser implements Action {
    readonly type = ActionTypes.DeleteSecurityUser;

    constructor(public payload: any) {}
}
export class DeleteSecurityUserConfirmationDismiss implements Action {
    readonly type = ActionTypes.DeleteSecurityUserConfirmationDismiss;
}

export class ResetAllSecurityUsers implements Action {
    readonly type = ActionTypes.ResetAllSecurityUsers;

    constructor(public payload: any) {}
}
export class ResetAllSecurityUsersConfirmationDismiss implements Action {
    readonly type = ActionTypes.ResetAllSecurityUsersConfirmationDismiss;
}

export class DeleteAllPINCodes implements Action {
    readonly type = ActionTypes.DeleteAllPINCodes;

    constructor(public payload: any) {}
}
export class DeleteAllPINCodesConfirmationDismiss implements Action {
    readonly type = ActionTypes.DeleteAllPINCodesConfirmationDismiss;
}

export class ClearZWaveDevicesState implements Action {
    readonly type = ActionTypes.ClearZWaveDevicesState;
}

export class GetZWaveDevices implements ZWaveDeviceAction {
    readonly type = ActionTypes.GetZWaveDevices;
    constructor(public readonly payload: ZWaveDevicePayload) {}
}

export class GetZWaveDevicesSuccess implements ZWaveDeviceSuccessAction {
    readonly type = ActionTypes.GetZWaveDevicesSuccess;
    constructor(public readonly result: ZWaveDeviceResultPayload) {}
}

export class GetZWaveDevicesFailure implements ZWaveDeviceFailureAction {
    readonly type = ActionTypes.GetZWaveDevicesFailure;
    constructor(public readonly error: ZWaveDeviceError) {}
}

export type Actions =
    | GetDevices
    | GetDevicesSilent
    | GetDevicesSuccess
    | GetDevicesFailure
    | GetDeviceInfoSuccess
    | ResetDevicesCallState
    | ClearDevicesState
    | UpdateDeviceName
    | UpdateDeviceNameSuccess
    | UpdateDeviceNameFailure
    | GetDeviceClassObjects
    | GetDeviceClassObjectsSuccess
    | GetDeviceClassObjectsFailure
    | GetDeviceClassObjectsForDevices
    | GetDeviceClassObjectsForDevicesSuccess
    | GetDeviceClassObjectsForDevicesFailure
    | GetDeviceClassObjectPropertySuccess
    | UpdateSecureDeviceClassObjectOperation
    | InvokeDeviceClassObjectOperation
    | InvokeDeviceClassObjectOperationSuccess
    | InvokeDeviceClassObjectOperationSilent
    | InvokeDeviceClassObjectOperationSilentSuccess
    | UpdateDeviceClassObject
    | UpdateDeviceClassObjectSuccess
    | UpdateDeviceClassObjectFailure
    | DeleteDevice
    | DeleteDeviceSuccess
    | DeleteDeviceFailure
    | DeleteDeviceConfirmation
    | DeleteDeviceConfirmationDismiss
    | DeleteAllDevices
    | DeleteAllDevicesSuccess
    | DeleteAllDevicesFailure
    | DeleteSensor
    | DeleteSensorSuccess
    | DeleteSensorFailure
    | DeleteSensorConfirmation
    | DeleteSensorConfirmationDismiss
    | ClearDCOState
    | ClearAllDCOs
    | ResetDCOState
    | ClearZWaveDevicesState
    | GetZWaveDevices
    | GetZWaveDevicesSuccess
    | GetZWaveDevicesFailure;
